import { useT } from "../../../../shared/i18n/i18n";
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { linter, type Diagnostic } from "@codemirror/lint";
import { EditorState, Prec, RangeSetBuilder, StateField } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  hoverTooltip,
  keymap,
  showTooltip,
  type DecorationSet,
  type Tooltip,
  type ViewUpdate,
  ViewPlugin,
  tooltips,
} from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { WorkObjectFieldDefinition } from "../../../../entities/work-object-field/model/use-work-object-fields";
import type { Translate } from "../../../../shared/i18n/i18n";
import { getWorkObjectFieldTypeMeta } from "../../../../entities/work-object-field/model/work-object-field-type-meta";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Braces, Calculator, ChevronRight, FunctionSquare, GitBranch, Loader2, TriangleAlert, type LucideIcon } from "lucide-react";

type FormulaExpressionEditorProps = {
  compact?: boolean;
  currentFieldId: string | null;
  disabled?: boolean;
  fields: WorkObjectFieldDefinition[];
  externalError?: string | null;
  onChange: (value: string) => void;
  onPreview?: (expression: string) => Promise<FormulaPreviewResult>;
  showValidationMessages?: boolean;
  scopeFieldIds?: string[];
  value: string;
};

export type FormulaPreviewSampleInput = {
  label: string;
  path: string;
  value: string;
};

export type FormulaPreviewResult = {
  dependencies: string[];
  error: string | null;
  sampleInputs: FormulaPreviewSampleInput[];
  value: string | null;
  warnings: string[];
  workObjectId: string | null;
};

type FormulaFunctionCategory = "logic" | "math" | "text" | "date";

type FormulaSuggestion = {
  category?: FormulaFunctionCategory;
  description: string;
  example?: string;
  group: "field" | "related" | "function";
  insertText: string;
  label: string;
  signature?: string;
};

type FormulaIssue = {
  from: number;
  message: string;
  path: string;
  to: number;
};

type FormulaCompletionTone = "field" | "relation" | "function";

type FormulaCompletionOption = {
  Icon: LucideIcon;
  apply: string;
  key: string;
  name: string;
  token: string;
  tone: FormulaCompletionTone;
  typeLabel: string;
};

type FormulaCompletionInput = {
  directFields: WorkObjectFieldDefinition[];
  relationFields: WorkObjectFieldDefinition[];
  sourceFields: WorkObjectFieldDefinition[];
};

type FormulaCompletionState = {
  coords: { bottom: number; left: number; top: number };
  from: number;
  options: FormulaCompletionOption[];
  selected: number;
  to: number;
};

function formulaFunction(
  category: FormulaFunctionCategory,
  label: string,
  signature: string,
  description: string,
  example: string,
): FormulaSuggestion {
  return { category, description, example, group: "function", insertText: `${label}()`, label, signature };
}

const formulaFunctions: FormulaSuggestion[] = [
  // Logic
  formulaFunction("logic", "if", "if(condition, then, else)", "Returns one value when the condition is true, another when false.", 'if({estimate} > 8 or {blocked}, "Large", "Small")'),
  formulaFunction("logic", "switch", "switch(value, case, result, …, default)", "Matches a value against cases and returns the paired result.", 'switch({priority}, "high", 3, "low", 1, 0)'),
  formulaFunction("logic", "isblank", "isblank(value)", "True when the value is empty.", "isblank({due})"),
  formulaFunction("logic", "coalesce", "coalesce(a, b, …)", "Returns the first non-empty value.", "coalesce({due}, {created})"),
  // Math
  formulaFunction("math", "sum", "sum(list)", "Adds numbers, including values from a related collection.", "sum({children.estimate})"),
  formulaFunction("math", "avg", "avg(list)", "Average of numeric values.", "avg({children.rating})"),
  formulaFunction("math", "min", "min(list)", "Smallest value.", "min({children.estimate})"),
  formulaFunction("math", "max", "max(list)", "Largest value.", "max({children.estimate})"),
  formulaFunction("math", "count", "count(list)", "Counts values in a collection.", "count({children.id})"),
  formulaFunction("math", "filled", "filled(list)", "Counts non-empty values.", "filled({children.due})"),
  formulaFunction("math", "empty", "empty(list)", "Counts empty values.", "empty({children.due})"),
  formulaFunction("math", "median", "median(list)", "Middle value of a collection.", "median({children.estimate})"),
  formulaFunction("math", "product", "product(list)", "Multiplies all values together.", "product({factors})"),
  formulaFunction("math", "round", "round(number, digits?)", "Rounds to the given number of decimal digits.", "round({rate}, 2)"),
  formulaFunction("math", "abs", "abs(number)", "Absolute value.", "abs({delta})"),
  formulaFunction("math", "ceiling", "ceiling(number)", "Rounds up to the nearest integer.", "ceiling({hours})"),
  formulaFunction("math", "floor", "floor(number)", "Rounds down to the nearest integer.", "floor({hours})"),
  formulaFunction("math", "mod", "mod(number, divisor)", "Remainder after division.", "mod({count}, 2)"),
  formulaFunction("math", "power", "power(base, exponent)", "Raises a number to a power.", "power({side}, 2)"),
  formulaFunction("math", "sqrt", "sqrt(number)", "Square root.", "sqrt({area})"),
  // Text
  formulaFunction("text", "concat", "concat(a, b, …)", "Joins text values together.", 'concat({key}, "-", {number})'),
  formulaFunction("text", "len", "len(text)", "Number of characters in the text.", "len({title})"),
  formulaFunction("text", "lower", "lower(text)", "Converts text to lowercase.", "lower({title})"),
  formulaFunction("text", "upper", "upper(text)", "Converts text to uppercase.", "upper({key})"),
  formulaFunction("text", "trim", "trim(text)", "Removes surrounding whitespace.", "trim({title})"),
  formulaFunction("text", "contains", "contains(text, search)", "True when the text contains the search string.", 'contains({title}, "urgent")'),
  formulaFunction("text", "left", "left(text, count)", "First N characters of the text.", "left({title}, 10)"),
  formulaFunction("text", "right", "right(text, count)", "Last N characters of the text.", "right({key}, 4)"),
  formulaFunction("text", "text", "text(value)", "Converts any value to text.", "text({estimate})"),
  formulaFunction("text", "join", "join(list, separator?)", "Joins the values of a list into text (default separator is a comma).", 'join({children.title}, ", ")'),
  // Date & time
  formulaFunction("date", "today", "today()", "The current date.", "days({due}, today())"),
  formulaFunction("date", "now", "now()", "The current date and time.", "now()"),
  formulaFunction("date", "date", "date(year, month, day)", "Builds a date from year, month and day.", "date(2026, 1, 15)"),
  formulaFunction("date", "days", "days(end, start)", "Whole days between two dates.", "days({due}, today())"),
  formulaFunction("date", "date_diff", "date_diff(end, start, unit?)", "Difference between two dates in a unit: days, hours, minutes, weeks, months, years (default days).", 'date_diff({due}, today(), "days")'),
  formulaFunction("date", "quarter", "quarter(date)", "Calendar quarter of a date (1–4).", "quarter({due})"),
  formulaFunction("date", "dateadd", "dateadd(date, amount, unit)", "Shifts a date forward by days, weeks, months or years (a negative amount shifts back).", 'dateadd({start}, 7, "days")'),
  formulaFunction("date", "datesubtract", "datesubtract(date, amount, unit)", "Shifts a date back by days, weeks, months or years (a negative amount shifts forward).", 'datesubtract({due}, 3, "days")'),
  formulaFunction("date", "year", "year(date)", "Year part of a date.", "year({due})"),
  formulaFunction("date", "month", "month(date)", "Month part of a date (1–12).", "month({due})"),
  formulaFunction("date", "day", "day(date)", "Day of the month.", "day({due})"),
  formulaFunction("date", "weekday", "weekday(date)", "Day of week (Mon=1 … Sun=7).", "weekday({due})"),
  formulaFunction("date", "workdays", "workdays(start, end)", "Working days between two dates.", "workdays({start}, {due})"),
  formulaFunction("date", "start_of", "start_of(date, unit)", "Truncates a date to the start of a unit: year, month, week, day, hour, minute.", 'start_of({due}, "month")'),
  formulaFunction("date", "end_of", "end_of(date, unit)", "The last moment of a unit — end of month, week, day, and so on.", 'end_of({due}, "month")'),
  formulaFunction("date", "format_date", "format_date(date, pattern?)", "Formats a date. Optional pattern with tokens YYYY YY MMMM MMM MM M DD D dddd ddd HH mm ss ([text] is literal).", 'format_date({due}, "DD.MM.YYYY")'),
];

export function FormulaExpressionEditor({
  compact = false,
  currentFieldId,
  disabled,
  externalError,
  fields,
  onChange,
  onPreview,
  showValidationMessages = true,
  scopeFieldIds,
  value,
}: FormulaExpressionEditorProps) {
  const t = useT();
  const editorRef = useRef<EditorView | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [preview, setPreview] = useState<FormulaPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const scopeFieldIdSet = useMemo(() => (scopeFieldIds ? new Set(scopeFieldIds) : null), [scopeFieldIds]);
  const scopedFields = useMemo(
    () => fields.filter((field) => !scopeFieldIdSet || scopeFieldIdSet.has(field.id)),
    [fields, scopeFieldIdSet],
  );
  const directFields = useMemo(
    () => scopedFields.filter((field) => field.id !== currentFieldId),
    [currentFieldId, scopedFields],
  );
  const relationFields = useMemo(
    () =>
      scopedFields.filter(
        (field) =>
          field.id !== currentFieldId &&
          (field.type === "work_object_relation" || field.type === "work_object_multi_relation"),
      ),
    [currentFieldId, scopedFields],
  );
  const relatedSourceFields = useMemo(
    () => scopedFields.filter((field) => field.id !== currentFieldId),
    [currentFieldId, scopedFields],
  );

  const validation = useMemo(
    () => validateFormulaExpression(value, fields, currentFieldId, t),
    [currentFieldId, fields, value],
  );

  const [completion, setCompletionState] = useState<FormulaCompletionState | null>(null);
  const completionRef = useRef<FormulaCompletionState | null>(null);
  const setCompletion = useCallback((next: FormulaCompletionState | null) => {
    completionRef.current = next;
    setCompletionState(next);
  }, []);

  const sampleInputsRef = useRef<FormulaPreviewSampleInput[]>([]);
  sampleInputsRef.current = preview?.sampleInputs ?? [];

  const completionInput = useMemo<FormulaCompletionInput>(
    () => ({ directFields, relationFields, sourceFields: relatedSourceFields }),
    [directFields, relatedSourceFields, relationFields],
  );

  const openCompletionAt = useCallback(
    (view: EditorView, explicit: boolean) => {
      if (!view.hasFocus) {
        setCompletion(null);
        return false;
      }
      const pos = view.state.selection.main.head;
      const result = computeFormulaCompletion(view.state, pos, completionInput, explicit, t);
      if (!result || result.options.length === 0) {
        setCompletion(null);
        return false;
      }
      const coords = view.coordsAtPos(result.from);
      if (!coords) {
        setCompletion(null);
        return false;
      }
      const previous = completionRef.current;
      const sameAnchor = previous?.from === result.from;
      const selected =
        sameAnchor && previous
          ? Math.min(previous.selected, result.options.length - 1)
          : 0;
      setCompletion({
        coords: { bottom: coords.bottom, left: coords.left, top: coords.top },
        from: result.from,
        options: result.options,
        selected,
        to: result.to,
      });
      return true;
    },
    [completionInput, setCompletion],
  );

  const moveSelection = useCallback(
    (delta: number) => {
      const current = completionRef.current;
      if (!current) {
        return false;
      }
      const count = current.options.length;
      const selected = (current.selected + delta + count) % count;
      setCompletion({ ...current, selected });
      return true;
    },
    [setCompletion],
  );

  const acceptSelected = useCallback(
    (view: EditorView) => {
      const current = completionRef.current;
      if (!current) {
        return false;
      }
      const option = current.options[current.selected];
      if (!option) {
        return false;
      }
      applyFormulaCompletion(view, option, current.from, current.to);
      return true;
    },
    [],
  );

  const closeCompletionOverlay = useCallback(() => {
    if (!completionRef.current) {
      return false;
    }
    setCompletion(null);
    return true;
  }, [setCompletion]);

  const editorExtensions = useMemo(
    () => [
      formulaSyntaxHighlighting(fields, currentFieldId),
      formulaDiagnostics(fields, currentFieldId, t),
      closeBrackets(),
      EditorView.updateListener.of((update) => {
        if (update.docChanged || update.selectionSet || update.focusChanged || update.geometryChanged) {
          openCompletionAt(update.view, false);
        }
      }),
      Prec.highest(
        keymap.of([
          { key: "Mod-Space", run: (view) => openCompletionAt(view, true) },
          { key: "Tab", run: acceptSelected },
          { key: "Enter", run: acceptSelected },
          { key: "Escape", run: closeCompletionOverlay },
          { key: "ArrowDown", run: () => moveSelection(1) },
          { key: "ArrowUp", run: () => moveSelection(-1) },
        ]),
      ),
      keymap.of(closeBracketsKeymap),
      EditorState.transactionFilter.of((tr) => (tr.newDoc.lines > 1 ? [] : tr)),
      formulaHoverTooltip(fields, currentFieldId, sampleInputsRef, t),
      formulaSignatureField,
      typeof document === "undefined" ? [] : tooltips({ parent: document.body }),
      formulaEditorTheme,
    ],
    [acceptSelected, closeCompletionOverlay, currentFieldId, fields, moveSelection, openCompletionAt],
  );

  function handleChange(nextValue: string) {
    onChange(nextValue);
  }

  function insertText(insertText: string) {
    const editor = editorRef.current;
    if (!editor) {
      handleChange(`${value}${value ? " " : ""}${insertText}`);
      return;
    }

    const range = editor.state.selection.main;
    editor.dispatch({
      changes: { from: range.from, insert: insertText, to: range.to },
      selection: { anchor: range.from + insertText.length - (insertText.endsWith(")") ? 1 : 0) },
      userEvent: "input.complete",
    });
    editor.focus();
  }

  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const hasIssues = validation.issues.length > 0;
  const trimmedValue = value.trim();

  useEffect(() => {
    const preview = onPreviewRef.current;
    if (!preview || hasIssues || trimmedValue.length === 0) {
      setPreview(null);
      setPreviewError(null);
      setIsPreviewing(false);
      return;
    }
    let cancelled = false;
    setIsPreviewing(true);
    const handle = setTimeout(() => {
      preview(trimmedValue)
        .then((result) => {
          if (!cancelled) {
            setPreview(result);
            setPreviewError(null);
          }
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            setPreview(null);
            setPreviewError(error instanceof Error ? error.message : "Formula preview failed");
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsPreviewing(false);
          }
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [hasIssues, trimmedValue]);

  return (
    <Field>
      {compact ? null : (
        <FieldLabel className="justify-between">
          <span>{t("formula.title")}</span>
          <span className="font-normal text-muted-foreground">
            {validation.dependencies.length} {validation.dependencies.length === 1 ? "dependency" : "dependencies"}
          </span>
        </FieldLabel>
      )}
      <div
        className={cn(
          "overflow-hidden rounded-md border border-input bg-input/20 transition-colors focus-within:border-ring",
          (Boolean(externalError) || validation.issues.length > 0) &&
            "border-destructive focus-within:border-destructive",
        )}
      >
        <FormulaLanguageEditor
          disabled={disabled}
          editorRef={editorRef}
          extensions={editorExtensions}
          onChange={handleChange}
          value={value}
        />
        <FormulaCompletionOverlay
          onHover={(index) => {
            const current = completionRef.current;
            if (current) {
              setCompletion({ ...current, selected: index });
            }
          }}
          onSelect={(index) => {
            const view = editorRef.current;
            const current = completionRef.current;
            const option = current?.options[index];
            if (view && current && option) {
              applyFormulaCompletion(view, option, current.from, current.to);
            }
          }}
          state={completion}
        />
        <div className="flex items-center gap-1.5 px-1.5 pb-1.5">
          <FormulaInsertPicker
            directFields={directFields}
            disabled={disabled}
            onInsert={insertText}
            relationFields={relationFields}
            sourceFields={relatedSourceFields}
          />
          {onPreview ? (
            <FormulaLivePreview
              error={previewError ?? preview?.error ?? null}
              isPreviewing={isPreviewing}
              ready={!disabled && validation.issues.length === 0 && trimmedValue.length > 0}
              sampleInputs={preview?.sampleInputs ?? []}
              value={preview?.value ?? null}
            />
          ) : null}
        </div>
      </div>
      {externalError ? (
        <FieldError>{externalError}</FieldError>
      ) : showValidationMessages && validation.issues.length > 0 ? (
        <div className="grid gap-1">
          {validation.issues.map((issue) => (
            <FieldError key={`${issue.path}-${issue.message}`}>{issue.message}</FieldError>
          ))}
        </div>
      ) : preview?.warnings && preview.warnings.length > 0 ? (
        <div className="grid gap-1">
          {preview.warnings.map((warning) => (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" key={warning}>
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span>{warning}</span>
            </p>
          ))}
        </div>
      ) : compact ? null : validation.dependencies.length > 0 ? (
        <FormulaDependencyPreview dependencies={validation.dependencies} />
      ) : (
        <FieldDescription>
          Use braces for fields, e.g. {"{estimate}"} or {"{children.estimate}"}. Fields that aren’t set on an item
          evaluate to empty, so formulas stay safe across mixed item types.
        </FieldDescription>
      )}
    </Field>
  );
}

function FormulaLanguageEditor({
  disabled,
  editorRef,
  extensions,
  onChange,
  value,
}: {
  disabled?: boolean;
  editorRef: MutableRefObject<EditorView | null>;
  extensions: Parameters<typeof CodeMirror>[0]["extensions"];
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <CodeMirror
      basicSetup={{
        autocompletion: false,
        bracketMatching: true,
        closeBrackets: false,
        defaultKeymap: true,
        foldGutter: false,
        highlightActiveLine: false,
        highlightActiveLineGutter: false,
        lineNumbers: false,
        searchKeymap: false,
      }}
      className="bg-transparent"
      editable={!disabled}
      extensions={extensions}
      height="auto"
      onChange={onChange}
      onCreateEditor={(editor) => {
        editorRef.current = editor;
      }}
      placeholder="sum({related_items.estimate}) + {estimate}"
      theme="none"
      value={value}
    />
  );
}

function FormulaLivePreview({
  error,
  isPreviewing,
  ready,
  sampleInputs,
  value,
}: {
  error: string | null;
  isPreviewing: boolean;
  ready: boolean;
  sampleInputs: FormulaPreviewSampleInput[];
  value: string | null;
}) {
  const t = useT();
  if (!ready) {
    return null;
  }
  if (isPreviewing) {
    return (
      <span className="flex h-6 items-center gap-1.5 rounded-sm border border-border/60 bg-muted/20 px-2 text-[0.6875rem] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        {t("formula.calculating")}
      </span>
    );
  }
  if (error) {
    return (
      <span
        className="flex h-6 min-w-0 items-center gap-1.5 rounded-sm border border-destructive/40 bg-destructive/10 px-2 text-[0.6875rem] text-destructive"
        title={error}
      >
        <Calculator className="size-3 shrink-0" />
        <span className="min-w-0 truncate">{error}</span>
      </span>
    );
  }

  const resultChip = (
    <span className="flex h-6 min-w-0 cursor-default items-center gap-1.5 rounded-sm border border-border/60 bg-muted/20 px-2 text-[0.6875rem]">
      <Calculator className="size-3 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">=</span>
      <span className="min-w-0 truncate font-mono text-foreground">{value ?? "—"}</span>
    </span>
  );

  if (sampleInputs.length === 0) {
    return resultChip;
  }

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger render={resultChip} />
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Positioner align="start" className="isolate z-50" side="top" sideOffset={6}>
          <TooltipPrimitive.Popup className="z-50 flex max-w-xs flex-col gap-1 rounded-lg bg-popover p-2 text-popover-foreground shadow-md outline-hidden ring-1 ring-foreground/10">
            <span className="mb-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground">
              {t("formula.sampleInputs")}
            </span>
            {sampleInputs.map((input) => (
              <span className="flex items-baseline justify-between gap-3 text-[0.6875rem]" key={input.path}>
                <span className="min-w-0 truncate text-muted-foreground">{input.label}</span>
                <span className="shrink-0 font-mono text-foreground">{input.value}</span>
              </span>
            ))}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

type FormulaLeaf = {
  Icon: LucideIcon;
  alias?: string;
  aliasExample?: string;
  description: string;
  example?: string;
  id: string;
  insertText: string;
  label: string;
  search: string;
  syntax: string;
  typeLabel?: string;
};

type FormulaSection = {
  Icon: LucideIcon;
  dividerBefore?: boolean;
  id: string;
  label: string;
  leaves: FormulaLeaf[];
};

const formulaCategoryLabels: Record<FormulaFunctionCategory, string> = {
  date: "Date & time",
  logic: "Logic",
  math: "Math",
  text: "Text",
};

// Must match the backend `formulaReverseRelationKey` so inserted reverse
// references resolve on the server.
function formulaReverseRelationKey(relationKey: string): string {
  return `ref_${relationKey}`;
}

// Fields that don't apply to every item type resolve to empty on items that
// don't have them — surface that so formulas stay predictable across a mixed board.
function formulaScopeNote(field: WorkObjectFieldDefinition, t: Translate): string {
  return field.appliesToAllTypes ? "" : t("formula.scopeNote");
}

const formulaFunctionCategoryOrder: FormulaFunctionCategory[] = ["logic", "math", "text", "date"];

const formulaOperators: {
  alias?: string;
  aliasExample?: string;
  descriptionKey: string;
  example: string;
  insertText: string;
  label: string;
  name: string;
  syntax: string;
}[] = [
  { descriptionKey: "formula.opAdd", example: "{estimate} + 2", insertText: " + ", label: "+", name: "Add", syntax: "a + b" },
  { descriptionKey: "formula.opSubtract", example: "{planned} - {done}", insertText: " - ", label: "-", name: "Subtract", syntax: "a - b" },
  { descriptionKey: "formula.opMultiply", example: "{rate} * {hours}", insertText: " * ", label: "*", name: "Multiply", syntax: "a * b" },
  { descriptionKey: "formula.opDivide", example: "{total} / {count}", insertText: " / ", label: "/", name: "Divide", syntax: "a / b" },
  { descriptionKey: "formula.opEqual", example: '{status} = "done"', insertText: " = ", label: "=", name: "Equals", syntax: "a = b" },
  { descriptionKey: "formula.opNotEqual", example: '{status} != "done"', insertText: " != ", label: "!=", name: "Not equal", syntax: "a != b" },
  { descriptionKey: "formula.opGreater", example: "{estimate} > 8", insertText: " > ", label: ">", name: "Greater than", syntax: "a > b" },
  { descriptionKey: "formula.opGreaterEqual", example: "{estimate} >= 8", insertText: " >= ", label: ">=", name: "Greater or equal", syntax: "a >= b" },
  { descriptionKey: "formula.opLess", example: "{estimate} < 8", insertText: " < ", label: "<", name: "Less than", syntax: "a < b" },
  { descriptionKey: "formula.opLessEqual", example: "{estimate} <= 8", insertText: " <= ", label: "<=", name: "Less or equal", syntax: "a <= b" },
  { alias: "&&", aliasExample: "{done} && {approved}", descriptionKey: "formula.opAnd", example: "{done} and {approved}", insertText: " and ", label: "and", name: "And", syntax: "a and b" },
  { alias: "||", aliasExample: "{blocked} || {overdue}", descriptionKey: "formula.opOr", example: "{blocked} or {overdue}", insertText: " or ", label: "or", name: "Or", syntax: "a or b" },
  { alias: "!", aliasExample: "!{done}", descriptionKey: "formula.opNot", example: "not {done}", insertText: "not ", label: "not", name: "Not", syntax: "not a" },
];

function buildFormulaSections(
  directFields: WorkObjectFieldDefinition[],
  relationFields: WorkObjectFieldDefinition[],
  sourceFields: WorkObjectFieldDefinition[],
  t: Translate,
): FormulaSection[] {
  const sections: FormulaSection[] = [];

  sections.push({
    Icon: Braces,
    id: "fields",
    label: t("formula.fields"),
    leaves: directFields.map((field) => {
      const meta = getWorkObjectFieldTypeMeta(field.type, t);
      return {
        Icon: meta.icon,
        description: t("formula.leafDirect", { field: field.name, scope: formulaScopeNote(field, t) }),
        id: field.id,
        insertText: `{${field.key}}`,
        label: field.name,
        search: `${field.name} ${field.key} ${meta.label}`,
        syntax: `{${field.key}}`,
        typeLabel: field.appliesToAllTypes ? meta.label : t("formula.typeSomeTypes", { type: meta.label }),
      };
    }),
  });

  for (const relation of relationFields) {
    const relationMeta = getWorkObjectFieldTypeMeta(relation.type, t);
    sections.push({
      Icon: relationMeta.icon,
      id: relation.id,
      label: relation.name,
      leaves: sourceFields
        .filter((field) => field.id !== relation.id)
        .map((field) => {
          const meta = getWorkObjectFieldTypeMeta(field.type, t);
          return {
            Icon: meta.icon,
            description: t("formula.leafViaRelation", { field: field.name, relation: relation.name, scope: formulaScopeNote(field, t) }),
            id: `${relation.id}-${field.id}`,
            insertText: `{${relation.key}.${field.key}}`,
            label: field.name,
            search: `${field.name} ${field.key} ${meta.label} ${relation.name}`,
            syntax: `{${relation.key}.${field.key}}`,
            typeLabel: t("formula.typeViaRelation", { relation: relation.name, type: meta.label }),
          };
        }),
    });
  }

  // "Referenced by" — reverse relations: items that point at this one.
  relationFields.forEach((relation, index) => {
    const reverseKey = formulaReverseRelationKey(relation.key);
    sections.push({
      Icon: GitBranch,
      dividerBefore: index === 0,
      id: `reverse-${relation.id}`,
      label: t("formula.referencedVia", { relation: relation.name }),
      leaves: sourceFields
        .filter((field) => field.id !== relation.id)
        .map((field) => {
          const meta = getWorkObjectFieldTypeMeta(field.type, t);
          return {
            Icon: meta.icon,
            description: t("formula.leafReverseRelation", { field: field.name, relation: relation.name, scope: formulaScopeNote(field, t) }),
            id: `reverse-${relation.id}-${field.id}`,
            insertText: `{${reverseKey}.${field.key}}`,
            label: field.name,
            search: `${field.name} ${field.key} ${meta.label} ${relation.name} referenced by backlink`,
            syntax: `{${reverseKey}.${field.key}}`,
            typeLabel: t("formula.typeReferencedBy", { relation: relation.name, type: meta.label }),
          };
        }),
    });
  });

  sections.push({
    Icon: Calculator,
    dividerBefore: true,
    id: "operators",
    label: t("formula.operators"),
    leaves: formulaOperators.map((operator) => ({
      Icon: Calculator,
      alias: operator.alias,
      aliasExample: operator.aliasExample,
      description: t(operator.descriptionKey),
      example: operator.example,
      id: `op-${operator.label}`,
      insertText: operator.insertText,
      label: operator.label,
      search: `${operator.label} ${operator.alias ?? ""} ${operator.name} ${t(operator.descriptionKey)}`,
      syntax: operator.syntax,
      typeLabel: operator.name,
    })),
  });

  formulaFunctionCategoryOrder.forEach((category) => {
    sections.push({
      Icon: FunctionSquare,
      id: `fn-${category}`,
      label: formulaCategoryLabels[category],
      leaves: formulaFunctions
        .filter((suggestion) => suggestion.category === category)
        .map((suggestion) => ({
          Icon: FunctionSquare,
          description: suggestion.description,
          example: suggestion.example,
          id: suggestion.label,
          insertText: suggestion.insertText,
          label: suggestion.label,
          search: `${suggestion.label} ${suggestion.signature ?? ""} ${suggestion.description}`,
          syntax: suggestion.signature ?? suggestion.insertText,
        })),
    });
  });

  return sections;
}

function FormulaInsertPicker({
  directFields,
  disabled,
  onInsert,
  relationFields,
  sourceFields,
}: {
  directFields: WorkObjectFieldDefinition[];
  disabled?: boolean;
  onInsert: (text: string) => void;
  relationFields: WorkObjectFieldDefinition[];
  sourceFields: WorkObjectFieldDefinition[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["fields"]));
  const [activeLeafId, setActiveLeafId] = useState<string | null>(null);

  const sections = useMemo(
    () => buildFormulaSections(directFields, relationFields, sourceFields, t),
    [directFields, relationFields, sourceFields, t],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = sections
    .map((section) => ({
      ...section,
      matches: normalizedQuery
        ? section.leaves.filter((leaf) => leaf.search.toLowerCase().includes(normalizedQuery))
        : section.leaves,
    }))
    .filter((section) => section.matches.length > 0);

  const allVisibleLeaves = visibleSections.flatMap((section) => section.matches);
  const activeLeaf = allVisibleLeaves.find((leaf) => leaf.id === activeLeafId) ?? allVisibleLeaves[0] ?? null;

  function toggleSection(id: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function insertLeaf(leaf: FormulaLeaf) {
    onInsert(leaf.insertText);
    setOpen(false);
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        render={<Button className="gap-1.5 rounded-sm" disabled={disabled} size="sm" type="button" variant="outline" />}
      >
        <Braces className="size-3" />
        {t("formula.insert")}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(40rem,calc(100vw-2rem))] overflow-hidden p-0" sideOffset={6}>
        <div className="grid h-[22rem] grid-cols-[13rem_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col border-r border-border/60">
            <div className="p-1.5">
              <Input
                className="h-8 text-xs"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("formula.search")}
                value={query}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-1.5">
              {visibleSections.length > 0 ? (
                visibleSections.map((section) => {
                  const SectionIcon = section.Icon;
                  const isExpanded = normalizedQuery.length > 0 || expanded.has(section.id);
                  return (
                    <div key={section.id}>
                      {section.dividerBefore ? <div className="mx-1 my-1 h-px bg-border/60" /> : null}
                      <button
                        className="flex w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                        onClick={() => toggleSection(section.id)}
                        type="button"
                      >
                        <ChevronRight
                          className={cn("size-3 shrink-0 transition-transform", isExpanded && "rotate-90")}
                        />
                        <SectionIcon className="size-3.5 shrink-0" />
                        <span className="min-w-0 flex-1 truncate font-medium">{section.label}</span>
                      </button>
                      {isExpanded
                        ? section.matches.map((leaf) => {
                            const LeafIcon = leaf.Icon;
                            const isActive = leaf.id === activeLeaf?.id;
                            return (
                              <button
                                className={cn(
                                  "flex w-full min-w-0 items-center gap-2 rounded-md py-1.5 pl-7 pr-2 text-left text-xs transition-colors",
                                  isActive ? "bg-muted text-foreground" : "text-foreground/90 hover:bg-muted/50",
                                )}
                                key={leaf.id}
                                onClick={() => insertLeaf(leaf)}
                                onMouseMove={() => {
                                  if (leaf.id !== activeLeafId) {
                                    setActiveLeafId(leaf.id);
                                  }
                                }}
                                type="button"
                              >
                                <LeafIcon className="size-3.5 shrink-0 text-muted-foreground" />
                                <span className="min-w-0 flex-1 truncate">
                                  {leaf.label}
                                  {leaf.alias ? <span className="text-muted-foreground"> ({leaf.alias})</span> : null}
                                </span>
                              </button>
                            );
                          })
                        : null}
                    </div>
                  );
                })
              ) : (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t("formula.noMatches")}</p>
              )}
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto p-4">
            {activeLeaf ? (
              <div className="flex flex-col gap-3">
                <div className="min-w-0">
                  <h4 className="truncate text-sm font-medium text-foreground">{activeLeaf.label}</h4>
                  {activeLeaf.typeLabel ? (
                    <p className="mt-0.5 text-[0.6875rem] text-muted-foreground">{activeLeaf.typeLabel}</p>
                  ) : null}
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{activeLeaf.description}</p>
                </div>
                <FormulaDetailBlock label={t("formula.syntax")} value={activeLeaf.syntax} />
                {activeLeaf.example ? <FormulaDetailBlock label={t("formula.example")} value={activeLeaf.example} /> : null}
                {activeLeaf.alias ? (
                  <>
                    <p className="text-[0.6875rem] leading-relaxed text-muted-foreground">
                      <code className="rounded bg-muted/50 px-1 py-0.5 font-mono text-foreground">
                        {activeLeaf.alias}
                      </code>{" "}
                      is an alias — works exactly the same as{" "}
                      <code className="rounded bg-muted/50 px-1 py-0.5 font-mono text-foreground">
                        {activeLeaf.label}
                      </code>
                      .
                    </p>
                    {activeLeaf.aliasExample ? (
                      <FormulaDetailBlock label={t("formula.exampleWithAlias", { alias: activeLeaf.alias ?? "" })} value={activeLeaf.aliasExample} />
                    ) : null}
                  </>
                ) : null}
                <div>
                  <Button
                    className="gap-1.5 rounded-sm"
                    onClick={() => insertLeaf(activeLeaf)}
                    size="sm"
                    type="button"
                  >
                    {t("formula.insert")}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t("formula.selectToSeeDetails")}</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FormulaDetailBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-[0.6875rem] font-medium text-muted-foreground">{label}</span>
      <code className="overflow-x-auto rounded-md bg-muted/50 px-2.5 py-1.5 font-mono text-xs text-foreground">
        {value}
      </code>
    </div>
  );
}

function FormulaDependencyPreview({ dependencies }: { dependencies: string[] }) {
  const t = useT();
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <Calculator className="size-3.5" />
        {t("formula.reads")}
      </span>
      {dependencies.map((dependency) => (
        <span
          className={cn(
            "inline-flex max-w-full items-center rounded-sm border border-border/60 bg-muted/20 px-1.5 py-0.5 font-mono text-[0.6875rem] text-foreground",
            dependency.includes(".") && "border-primary/25 bg-primary/10",
          )}
          key={dependency}
        >
          {dependency}
        </span>
      ))}
    </div>
  );
}

function computeFormulaCompletion(
  state: EditorState,
  pos: number,
  input: FormulaCompletionInput,
  explicit: boolean,
  t: Translate,
): { from: number; options: FormulaCompletionOption[]; to: number } | null {
  const beforeCursor = state.sliceDoc(0, pos);
  const openBraceIndex = beforeCursor.lastIndexOf("{");
  const closeBraceIndex = beforeCursor.lastIndexOf("}");

  if (openBraceIndex > closeBraceIndex) {
    const referenceDraft = beforeCursor.slice(openBraceIndex + 1);
    const from = openBraceIndex + 1;
    // Replace the whole reference token, not just up to the caret, so picking
    // another field swaps the entire `{…}` content instead of splicing it.
    const to = referenceTokenEnd(state, pos);
    const dotIndex = referenceDraft.indexOf(".");
    if (dotIndex >= 0) {
      const relationQuery = referenceDraft.slice(0, dotIndex).trim().toLowerCase();
      const fieldQuery = referenceDraft.slice(dotIndex + 1).trim().toLowerCase();
      const relationField = input.relationFields.find(
        (field) => field.key.toLowerCase() === relationQuery || field.name.toLowerCase() === relationQuery,
      );
      // Reverse ("referenced by") relation: `ref_<relationKey>`.
      const reverseRelation =
        !relationField && relationQuery.startsWith("ref_")
          ? input.relationFields.find((field) => field.key.toLowerCase() === relationQuery.slice(4))
          : null;

      if (!relationField && !reverseRelation) {
        const options = [
          ...input.relationFields.filter((field) => matchesFormulaFieldQuery(field, relationQuery, t)),
        ].map((field) => ({
          Icon: GitBranch,
          apply: `${field.key}.`,
          key: field.id,
          name: field.name,
          token: `${field.key}.`,
          tone: "relation" as const,
          typeLabel: "Relation",
        }));
        const reverseOptions = input.relationFields
          .filter((field) => `ref_${field.key} ${field.name} referenced by`.toLowerCase().includes(relationQuery))
          .map((field) => ({
            Icon: GitBranch,
            apply: `${formulaReverseRelationKey(field.key)}.`,
            key: `reverse-${field.id}`,
            name: `Referenced via ${field.name}`,
            token: `${formulaReverseRelationKey(field.key)}.`,
            tone: "relation" as const,
            typeLabel: "Referenced by",
          }));
        const combined = [...options, ...reverseOptions];
        return combined.length > 0 ? { from, options: combined, to } : null;
      }

      const activeRelation = relationField ?? reverseRelation;
      if (!activeRelation) {
        return null;
      }
      const relationKeyForToken = relationField ? activeRelation.key : formulaReverseRelationKey(activeRelation.key);
      const typeLabel = relationField ? `via ${activeRelation.name}` : `referenced by ${activeRelation.name}`;
      const options = input.sourceFields
        .filter((field) => field.id !== activeRelation.id)
        .filter((field) => !fieldQuery || matchesFormulaFieldQuery(field, fieldQuery, t))
        .map((field) => {
          const meta = getWorkObjectFieldTypeMeta(field.type, t);
          return {
            Icon: meta.icon,
            apply: `${relationKeyForToken}.${field.key}`,
            key: `${activeRelation.id}-${field.id}`,
            name: field.name,
            token: `${relationKeyForToken}.${field.key}`,
            tone: "relation" as const,
            typeLabel,
          };
        });
      return options.length > 0 ? { from, options, to } : null;
    }

    const query = referenceDraft.trim().toLowerCase();
    const options = input.directFields
      .filter((field) => matchesFormulaFieldQuery(field, query, t))
      .map((field) => {
        const meta = getWorkObjectFieldTypeMeta(field.type, t);
        return {
          Icon: meta.icon,
          apply: field.key,
          key: field.id,
          name: field.name,
          token: `{${field.key}}`,
          tone: "field" as const,
          typeLabel: meta.label,
        };
      });
    return options.length > 0 ? { from, options, to } : null;
  }

  const word = matchWordBefore(state, pos);
  if (!explicit && word.text.length === 0) {
    return null;
  }
  const functionQuery = word.text.toLowerCase();
  const options = formulaFunctions
    .filter((suggestion) => suggestion.label.startsWith(functionQuery))
    .map((suggestion) => ({
      Icon: FunctionSquare,
      apply: suggestion.label,
      key: suggestion.label,
      name: suggestion.label,
      token: suggestion.signature ?? `${suggestion.label}()`,
      tone: "function" as const,
      typeLabel: "Function",
    }));
  return options.length > 0 ? { from: word.from, options, to: identifierEnd(state, pos) } : null;
}

function matchWordBefore(state: EditorState, pos: number): { from: number; text: string } {
  const line = state.doc.lineAt(pos);
  const before = state.sliceDoc(line.from, pos);
  const match = /[a-zA-Z_][a-zA-Z0-9_]*$/.exec(before);
  if (!match) {
    return { from: pos, text: "" };
  }
  return { from: pos - match[0].length, text: match[0] };
}

// End of the `{…}` reference token that contains the caret: the contiguous run
// of reference characters after the caret, stopping at whitespace or a brace.
function referenceTokenEnd(state: EditorState, pos: number): number {
  const line = state.doc.lineAt(pos);
  const after = state.sliceDoc(pos, line.to);
  const match = /^[^\s{}]*/.exec(after);
  return pos + (match ? match[0].length : 0);
}

// End of the identifier under the caret (for function-name completion).
function identifierEnd(state: EditorState, pos: number): number {
  const line = state.doc.lineAt(pos);
  const after = state.sliceDoc(pos, line.to);
  const match = /^[a-zA-Z0-9_]*/.exec(after);
  return pos + (match ? match[0].length : 0);
}

function applyFormulaCompletion(view: EditorView, option: FormulaCompletionOption, from: number, to: number) {
  const nextChar = view.state.sliceDoc(to, to + 1);

  if (option.tone === "function") {
    // Replace only the function name. Add "()" only when a "(" is not already
    // present, and drop the caret inside the parentheses in both cases.
    const hasParen = nextChar === "(";
    const insert = hasParen ? option.apply : `${option.apply}()`;
    view.dispatch({
      changes: { from, insert, to },
      selection: { anchor: from + option.apply.length + 1 },
      userEvent: "input.complete",
    });
    view.focus();
    return;
  }

  const insert = option.apply;
  const endsWithDot = insert.endsWith(".");
  let anchor = from + insert.length;
  if (!endsWithDot && nextChar === "}") {
    anchor += 1;
  }
  view.dispatch({
    changes: { from, insert, to },
    selection: { anchor },
    userEvent: "input.complete",
  });
  view.focus();
}

const formulaCompletionToneStyles: Record<FormulaCompletionTone, { badge: string; icon: string }> = {
  field: {
    badge: "bg-primary/10 text-primary/90",
    icon: "bg-primary/15 text-primary",
  },
  relation: {
    badge: "bg-emerald-500/12 text-emerald-300",
    icon: "bg-emerald-500/15 text-emerald-300",
  },
  function: {
    badge: "bg-violet-500/12 text-violet-300",
    icon: "bg-violet-500/15 text-violet-300",
  },
};

function FormulaCompletionOverlay({
  onHover,
  onSelect,
  state,
}: {
  onHover: (index: number) => void;
  onSelect: (index: number) => void;
  state: FormulaCompletionState | null;
}) {
  const t = useT();
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [state?.selected, state?.from]);

  if (!state || typeof document === "undefined") {
    return null;
  }

  const width = 320;
  const gap = 4;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const left = Math.max(8, Math.min(state.coords.left, viewportWidth - width - 8));
  const estimatedHeight = Math.min(state.options.length * 40 + 8, 264);
  const openUp = state.coords.bottom + gap + estimatedHeight > viewportHeight && state.coords.top > estimatedHeight;
  const top = openUp ? undefined : state.coords.bottom + gap;
  const bottom = openUp ? viewportHeight - state.coords.top + gap : undefined;

  return createPortal(
    <div
      className="fixed z-50 overflow-hidden rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
      onMouseDown={(event) => event.preventDefault()}
      role="listbox"
      style={{ bottom, left, maxHeight: 264, top, width }}
    >
      <div className="max-h-[15.5rem] overflow-y-auto">
        {state.options.map((option, index) => {
          const isSelected = index === state.selected;
          const tone = formulaCompletionToneStyles[option.tone];
          const Icon = option.Icon;
          return (
            <button
              aria-selected={isSelected}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors",
                isSelected ? "bg-muted" : "hover:bg-muted/60",
              )}
              key={option.key}
              onClick={() => onSelect(index)}
              onMouseMove={() => {
                if (!isSelected) {
                  onHover(index);
                }
              }}
              ref={isSelected ? selectedRef : undefined}
              role="option"
              type="button"
            >
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-md",
                  tone.icon,
                )}
              >
                <Icon className="size-3.5" />
              </span>
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span className="truncate text-[0.8125rem] font-medium leading-tight text-foreground">
                  {option.name}
                </span>
                <span className="truncate font-mono text-[0.6875rem] leading-tight text-muted-foreground">
                  {option.token}
                </span>
              </span>
              <span
                className={cn(
                  "ml-auto max-w-[9rem] shrink-0 truncate rounded px-1.5 py-0.5 text-[0.625rem] font-medium",
                  tone.badge,
                )}
              >
                {option.typeLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

function matchesFormulaFieldQuery(field: WorkObjectFieldDefinition, query: string, t: Translate) {
  if (!query) {
    return true;
  }
  const meta = getWorkObjectFieldTypeMeta(field.type, t);
  return `${field.key} ${field.name} ${meta.label}`.toLowerCase().includes(query);
}

// ---- Hover: demo value + type for a {reference} token ----

function describeFormulaReference(
  path: string,
  fieldsByKey: Map<string, WorkObjectFieldDefinition>,
  currentFieldId: string | null,
  t: Translate,
): { name: string; typeLabel: string; valid: boolean } {
  const parts = path.split(".").map((part) => part.trim());
  const leafKey = parts[parts.length - 1] ?? "";
  const leaf = fieldsByKey.get(leafKey);
  if (!leaf) {
    return { name: path, typeLabel: "Unknown field", valid: false };
  }
  const meta = getWorkObjectFieldTypeMeta(leaf.type, t);
  const scopeSuffix = leaf.appliesToAllTypes ? "" : " · some item types (empty elsewhere)";
  if (parts.length === 1) {
    return { name: leaf.name, typeLabel: `${meta.label}${scopeSuffix}`, valid: leaf.id !== currentFieldId };
  }
  const hops = parts.slice(0, -1).map((segment) => {
    if (segment.startsWith("ref_")) {
      const relation = fieldsByKey.get(segment.slice(4));
      return relation ? `↩ ${relation.name}` : segment;
    }
    const relation = fieldsByKey.get(segment);
    return relation ? relation.name : segment;
  });
  return {
    name: leaf.name,
    typeLabel: `${meta.label} · via ${hops.join(" → ")}${scopeSuffix}`,
    valid: leaf.id !== currentFieldId,
  };
}

function buildFormulaHoverDom(
  info: { name: string; typeLabel: string; valid: boolean },
  sample: FormulaPreviewSampleInput | undefined,
): HTMLElement {
  const root = document.createElement("div");
  root.className = "cm-formula-hover";

  const name = document.createElement("div");
  name.className = "cm-formula-hover-name";
  name.textContent = info.name;
  root.append(name);

  const type = document.createElement("div");
  type.className = "cm-formula-hover-type";
  type.textContent = info.valid ? info.typeLabel : `${info.typeLabel} · not available here`;
  root.append(type);

  if (sample) {
    const value = document.createElement("div");
    value.className = "cm-formula-hover-value";
    const label = document.createElement("span");
    label.className = "cm-formula-hover-value-label";
    label.textContent = "Sample";
    const data = document.createElement("span");
    data.className = "cm-formula-hover-value-data";
    data.textContent = sample.value;
    value.append(label, data);
    root.append(value);
  }

  return root;
}

function formulaHoverTooltip(
  fields: WorkObjectFieldDefinition[],
  currentFieldId: string | null,
  sampleInputsRef: MutableRefObject<FormulaPreviewSampleInput[]>,
  t: Translate,
) {
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  return hoverTooltip(
    (view, pos) => {
      const text = view.state.doc.toString();
      const regex = /\{([^{}]*)\}/g;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (pos < start || pos > end) {
          continue;
        }
        const path = (match[1] ?? "").trim();
        if (!path) {
          return null;
        }
        const info = describeFormulaReference(path, fieldsByKey, currentFieldId, t);
        const sample = sampleInputsRef.current.find((entry) => entry.path === path);
        return {
          above: true,
          create: () => ({ dom: buildFormulaHoverDom(info, sample) }),
          end,
          pos: start,
        };
      }
      return null;
    },
    { hideOnChange: true, hoverTime: 150 },
  );
}

// ---- Signature help while typing inside fn(…) ----

const formulaSignatureByLabel = new Map(formulaFunctions.map((suggestion) => [suggestion.label, suggestion]));

function activeFormulaCall(state: EditorState): { argIndex: number; from: number; name: string } | null {
  const pos = state.selection.main.head;
  const text = state.sliceDoc(0, pos);
  let depth = 0;
  let argIndex = 0;
  let index = text.length - 1;
  while (index >= 0) {
    const char = text[index];
    if (char === '"' || char === "'") {
      index -= 1;
      while (index >= 0 && text[index] !== char) {
        index -= 1;
      }
      index -= 1;
      continue;
    }
    if (char === "}") {
      index -= 1;
      while (index >= 0 && text[index] !== "{") {
        index -= 1;
      }
      index -= 1;
      continue;
    }
    if (char === ")") {
      depth += 1;
      index -= 1;
      continue;
    }
    if (char === "(") {
      if (depth === 0) {
        const identifier = /([a-zA-Z_][a-zA-Z0-9_]*)\s*$/.exec(text.slice(0, index));
        if (identifier?.[1]) {
          return { argIndex, from: index - identifier[1].length, name: identifier[1].toLowerCase() };
        }
        return null;
      }
      depth -= 1;
      index -= 1;
      continue;
    }
    if (char === "," && depth === 0) {
      argIndex += 1;
    }
    index -= 1;
  }
  return null;
}

function buildFormulaSignatureDom(suggestion: FormulaSuggestion, argIndex: number): HTMLElement {
  const root = document.createElement("div");
  root.className = "cm-formula-signature";

  const signature = suggestion.signature ?? `${suggestion.label}()`;
  const line = document.createElement("div");
  line.className = "cm-formula-signature-line";
  const open = signature.indexOf("(");
  const args = signature
    .slice(open + 1, signature.lastIndexOf(")"))
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  line.append(document.createTextNode(`${signature.slice(0, open)}(`));
  args.forEach((arg, index) => {
    const argEl = document.createElement("span");
    argEl.textContent = arg;
    const isVariadicTail = index === args.length - 1 && arg.includes("…");
    if (index === argIndex || (isVariadicTail && argIndex >= args.length - 1)) {
      argEl.className = "cm-formula-signature-active";
    }
    line.append(argEl);
    if (index < args.length - 1) {
      line.append(document.createTextNode(", "));
    }
  });
  line.append(document.createTextNode(")"));
  root.append(line);

  const description = document.createElement("div");
  description.className = "cm-formula-signature-desc";
  description.textContent = suggestion.description;
  root.append(description);

  return root;
}

function computeFormulaSignatureTooltip(state: EditorState): Tooltip | null {
  const call = activeFormulaCall(state);
  if (!call) {
    return null;
  }
  const suggestion = formulaSignatureByLabel.get(call.name);
  if (!suggestion) {
    return null;
  }
  return {
    above: true,
    create: () => ({ dom: buildFormulaSignatureDom(suggestion, call.argIndex) }),
    pos: call.from,
  };
}

const formulaSignatureField = StateField.define<Tooltip | null>({
  create: (state) => computeFormulaSignatureTooltip(state),
  update(value, transaction) {
    if (!transaction.docChanged && !transaction.selection) {
      return value;
    }
    return computeFormulaSignatureTooltip(transaction.state);
  },
  provide: (field) => showTooltip.from(field),
});

function formulaSyntaxHighlighting(fields: WorkObjectFieldDefinition[], currentFieldId: string | null) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildFormulaDecorations(view, fields, currentFieldId);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildFormulaDecorations(update.view, fields, currentFieldId);
        }
      }
    },
    {
      decorations: (plugin) => plugin.decorations,
    },
  );
}

function buildFormulaDecorations(
  view: EditorView,
  fields: WorkObjectFieldDefinition[],
  currentFieldId: string | null,
) {
  const builder = new RangeSetBuilder<Decoration>();
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  const text = view.state.doc.toString();
  const tokenRegex = /\{[^{}]*\}|"[^"]*"|'[^']*'|&&|\|\||\b(?:and|or|not)\b|\b[a-zA-Z_][a-zA-Z0-9_]*(?=\s*\()|\b\d+(?:\.\d+)?\b|[+\-*/=<>!,()]/g;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(text)) !== null) {
    const token = match[0];
    const className = getFormulaTokenClassName(token, fieldsByKey, currentFieldId);
    builder.add(match.index, match.index + token.length, Decoration.mark({ class: className }));
  }

  return builder.finish();
}

function getFormulaTokenClassName(
  token: string,
  fieldsByKey: Map<string, WorkObjectFieldDefinition>,
  currentFieldId: string | null,
) {
  if (token.startsWith("{") && token.endsWith("}")) {
    const reference = token.slice(1, -1).trim();
    const parts = reference.split(".");
    const field = fieldsByKey.get(parts[0] ?? "");
    const relatedField = parts.length === 2 ? fieldsByKey.get(parts[1] ?? "") : null;
    const isValidDirect = parts.length === 1 && field && field.id !== currentFieldId;
    const isValidRelated =
      parts.length === 2 &&
      field &&
      relatedField &&
      field.id !== currentFieldId &&
      relatedField.id !== currentFieldId &&
      (field.type === "work_object_relation" || field.type === "work_object_multi_relation");

    if (isValidDirect) {
      return "cm-formula-reference";
    }
    if (isValidRelated) {
      return "cm-formula-related-reference";
    }
    return "cm-formula-invalid-reference";
  }
  if (token.startsWith("\"") || token.startsWith("'")) {
    return "cm-formula-string";
  }
  if (/^\d/.test(token)) {
    return "cm-formula-number";
  }
  if (/^(?:&&|\|\||and|or|not)$/i.test(token)) {
    return "cm-formula-operator";
  }
  if (formulaFunctions.some((func) => func.label === token.toLowerCase())) {
    return "cm-formula-function";
  }
  if (/^[+\-*/=<>!,()]$/.test(token)) {
    return "cm-formula-operator";
  }
  return "";
}

function formulaDiagnostics(fields: WorkObjectFieldDefinition[], currentFieldId: string | null, t: Translate) {
  return linter(
    (view) =>
      validateFormulaExpression(view.state.doc.toString(), fields, currentFieldId, t).issues.map(
        (issue) =>
          ({
            from: issue.from,
            message: issue.message,
            severity: "error",
            source: "formula",
            to: issue.to,
          }) satisfies Diagnostic,
      ),
    {
      delay: 120,
      tooltipFilter: (diagnostics) => diagnostics.slice(0, 3),
    },
  );
}

const formulaEditorTheme = EditorView.theme({
  "&": {
    background: "transparent",
    color: "var(--foreground)",
    fontSize: "0.75rem",
  },
  ".cm-content": {
    caretColor: "var(--foreground)",
    fontFamily: "var(--font-mono)",
    lineHeight: "1.375rem",
    padding: "0.4375rem 0.625rem",
    whiteSpace: "pre",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "var(--foreground)",
    borderLeftWidth: "1.5px",
  },
  ".cm-editor": {
    background: "transparent",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-formula-function": {
    color: "#c4b5fd",
  },
  ".cm-formula-invalid-reference": {
    backgroundColor: "color-mix(in oklch, var(--destructive) 16%, transparent)",
    borderRadius: "0.125rem",
    color: "var(--destructive)",
    padding: "0 0.125rem",
  },
  ".cm-formula-number": {
    color: "#7dd3fc",
  },
  ".cm-formula-operator": {
    color: "var(--muted-foreground)",
  },
  ".cm-formula-reference": {
    backgroundColor: "color-mix(in oklch, var(--primary) 18%, transparent)",
    borderRadius: "0.125rem",
    color: "var(--primary)",
    padding: "0 0.125rem",
  },
  ".cm-formula-related-reference": {
    backgroundColor: "rgb(16 185 129 / 0.12)",
    borderRadius: "0.125rem",
    color: "rgb(110 231 183)",
    padding: "0 0.125rem",
  },
  ".cm-formula-string": {
    color: "#fcd34d",
  },
  ".cm-lintRange-error": {
    backgroundImage: "linear-gradient(45deg, transparent 65%, var(--destructive) 65%, var(--destructive) 80%, transparent 80%)",
    backgroundPosition: "0 100%",
    backgroundRepeat: "repeat-x",
    backgroundSize: "0.45rem 0.18rem",
    paddingBottom: "0.08rem",
  },
  ".cm-lintRange-error:hover": {
    backgroundColor: "color-mix(in oklch, var(--destructive) 12%, transparent)",
  },
  ".cm-gutters": {
    display: "none",
  },
  ".cm-line": {
    padding: 0,
  },
  ".cm-placeholder": {
    color: "var(--muted-foreground)",
    opacity: "0.55",
  },
  ".cm-scroller": {
    fontFamily: "var(--font-mono)",
    overflow: "auto",
  },
  ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
    backgroundColor: "color-mix(in oklch, var(--primary) 32%, transparent)",
  },
  ".cm-tooltip": {
    backgroundColor: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: "0.5rem",
    boxShadow: "0 12px 32px rgb(0 0 0 / 0.28)",
    color: "var(--popover-foreground)",
  },
  ".cm-tooltip-lint": {
    fontFamily: "var(--font-sans)",
    fontSize: "0.75rem",
    lineHeight: "1rem",
    maxWidth: "22rem",
    padding: "0.5rem 0.625rem",
  },
  ".cm-formula-hover": {
    display: "flex",
    flexDirection: "column",
    gap: "0.125rem",
    maxWidth: "18rem",
    padding: "0.5rem 0.625rem",
  },
  ".cm-formula-hover-name": {
    color: "var(--foreground)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.75rem",
    fontWeight: "500",
  },
  ".cm-formula-hover-type": {
    color: "var(--muted-foreground)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.6875rem",
  },
  ".cm-formula-hover-value": {
    alignItems: "baseline",
    display: "flex",
    gap: "0.375rem",
    marginTop: "0.25rem",
  },
  ".cm-formula-hover-value-label": {
    color: "var(--muted-foreground)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.5625rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  ".cm-formula-hover-value-data": {
    color: "var(--foreground)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.6875rem",
  },
  ".cm-formula-signature": {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    maxWidth: "22rem",
    padding: "0.5rem 0.625rem",
  },
  ".cm-formula-signature-line": {
    color: "var(--muted-foreground)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.75rem",
  },
  ".cm-formula-signature-active": {
    color: "var(--foreground)",
    fontWeight: "600",
  },
  ".cm-formula-signature-desc": {
    color: "var(--muted-foreground)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.6875rem",
    lineHeight: "0.9375rem",
  },
}, { dark: true });

function validateFormulaExpression(
  expression: string,
  fields: WorkObjectFieldDefinition[],
  currentFieldId: string | null,
  t: Translate,
): { dependencies: string[]; issues: FormulaIssue[] } {
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  const dependencies: string[] = [];
  const issues: FormulaIssue[] = [];
  const matches = [...expression.matchAll(/\{([^{}]*)\}/g)];
  const hasUnbalancedBraces = (expression.match(/\{/g)?.length ?? 0) !== (expression.match(/\}/g)?.length ?? 0);

  if (hasUnbalancedBraces) {
    const lastOpenBrace = expression.lastIndexOf("{");
    const lastCloseBrace = expression.lastIndexOf("}");
    const from = Math.max(0, Math.max(lastOpenBrace, lastCloseBrace));
    issues.push({
      from,
      message: t("formula.errUnfinished"),
      path: "braces",
      to: Math.max(from + 1, expression.length),
    });
  }

  for (const match of matches) {
    const from = match.index;
    const to = match.index + match[0].length;
    const path = match[1]?.trim() ?? "";
    if (!path) {
      issues.push({ from, message: t("formula.errEmptyReference"), path, to });
      continue;
    }

    const parts = path.split(".").map((part) => part.trim());
    if (parts.some((part) => part.length === 0)) {
      issues.push({ from, message: `Related path "${path}" is incomplete.`, path, to });
      continue;
    }
    if (parts.length === 1) {
      const field = fieldsByKey.get(parts[0] ?? "");
      if (!field) {
        issues.push({ from, message: `Field "${path}" was not found.`, path, to });
        continue;
      }
      if (field.id === currentFieldId) {
        issues.push({ from, message: t("formula.errSelfReference"), path, to });
        continue;
      }
      dependencies.push(field.key);
      continue;
    }

    if (parts.length !== 2) {
      issues.push({ from, message: `Related path "${path}" must use one relation and one field.`, path, to });
      continue;
    }

    const relationField = fieldsByKey.get(parts[0] ?? "");
    const sourceField = fieldsByKey.get(parts[1] ?? "");
    if (!relationField) {
      issues.push({ from, message: `Relation field "${parts[0]}" was not found.`, path, to });
      continue;
    }
    if (relationField.type !== "work_object_relation" && relationField.type !== "work_object_multi_relation") {
      issues.push({
        from,
        message: `"${relationField.key}" has no nested fields. Use dot only after a related item field.`,
        path,
        to,
      });
      continue;
    }
    if (!sourceField) {
      issues.push({ from, message: `Related field "${parts[1]}" was not found.`, path, to });
      continue;
    }
    if (sourceField.id === currentFieldId) {
      issues.push({ from, message: t("formula.errSelfThroughRelation"), path, to });
      continue;
    }
    dependencies.push(`${relationField.key}.${sourceField.key}`);
  }

  return {
    dependencies: [...new Set(dependencies)],
    issues,
  };
}
